import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { comoErroDaApi } from '../../../shared/api/erros';
import { diferencasDeCampos } from './diffJson';
import { ItensCitados } from './ItensCitados';
import { PaginaDoContrato } from './PaginaDoContrato';
import { ROTULOS_DE_CONFIANCA, ROTULOS_DE_TIPO_DE_REGRA, type RegraDaFila } from './api/tipos';

function textoDeJson(valor: unknown): string {
  return JSON.stringify(valor, null, 2);
}

/** `null` = JSON inválido (bloqueia o envio). */
function lerJson(texto: string): { valor: unknown } | null {
  try {
    return { valor: JSON.parse(texto) as unknown };
  } catch {
    return null;
  }
}

/**
 * O detalhe de UMA regra da fila, com o trecho destacado na página e as
 * três ações (`TP-050`, T9, D6): aprovar, ajustar (mostra o diff antes de
 * confirmar) e rejeitar (motivo obrigatório).
 */
export function RevisaoDaRegra({
  regra,
  aoAprovar,
  aoRejeitar,
  aoAjustar,
}: {
  regra: RegraDaFila;
  aoAprovar: () => Promise<unknown>;
  aoRejeitar: (motivo: string) => Promise<unknown>;
  aoAjustar: (escopo: unknown, efeito: unknown) => Promise<unknown>;
}): React.JSX.Element {
  const [acaoEmCurso, definirAcaoEmCurso] = useState<'aprovar' | 'rejeitar' | 'ajustar' | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  const [motivoDaRejeicao, definirMotivoDaRejeicao] = useState('');

  const [ajustando, definirAjustando] = useState(false);
  const [escopoEditado, definirEscopoEditado] = useState(() => textoDeJson(regra.escopo));
  const [efeitoEditado, definirEfeitoEditado] = useState(() => textoDeJson(regra.efeito));

  const executar = (acao: 'aprovar' | 'rejeitar' | 'ajustar', tarefa: () => Promise<unknown>): void => {
    definirAcaoEmCurso(acao);
    definirErro(null);
    tarefa()
      .catch((causa: unknown) => {
        definirErro(comoErroDaApi(causa).message);
      })
      .finally(() => {
        definirAcaoEmCurso(null);
      });
  };

  const escopoLido = lerJson(escopoEditado);
  const efeitoLido = efeitoEditado.trim() === '' ? { valor: null } : lerJson(efeitoEditado);
  const diferencas =
    escopoLido !== null && efeitoLido !== null
      ? [
          ...diferencasDeCampos(regra.escopo, escopoLido.valor),
          ...diferencasDeCampos(regra.efeito, efeitoLido.valor),
        ]
      : [];

  return (
    <Stack spacing={2} aria-label="Revisão da regra">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h3">{ROTULOS_DE_TIPO_DE_REGRA[regra.tipo]}</Typography>
        <Chip size="small" label={`Confiança ${ROTULOS_DE_CONFIANCA[regra.confianca]}`} />
      </Stack>

      <PaginaDoContrato
        fonteId={regra.fonteId}
        pagina={regra.pagina}
        trecho={regra.trecho}
        trechoLocalizado={regra.trechoLocalizado}
      />

      {regra.observacoes !== null && regra.observacoes.trim() !== '' ? (
        <Alert severity="info">{regra.observacoes}</Alert>
      ) : null}

      <Box>
        <Typography variant="subtitle2">Onde vale</Typography>
        <Box component="pre" sx={{ m: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
          {textoDeJson(regra.escopo)}
        </Box>
        {regra.efeito !== null ? (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              O que faz
            </Typography>
            <Box component="pre" sx={{ m: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
              {textoDeJson(regra.efeito)}
            </Box>
          </>
        ) : null}
      </Box>

      <ItensCitados escopo={regra.escopo} efeito={regra.efeito} descricoes={regra.descricoesDosItens} />

      {erro !== null ? <Alert severity="error" role="alert">{erro}</Alert> : null}

      {!ajustando ? (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="success"
            disabled={acaoEmCurso !== null}
            onClick={() => {
              executar('aprovar', aoAprovar);
            }}
          >
            Aprovar
          </Button>
          <Button
            variant="outlined"
            disabled={acaoEmCurso !== null}
            onClick={() => {
              definirAjustando(true);
            }}
          >
            Ajustar
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.5} component="form" aria-label="Ajustar a regra">
          <Typography variant="body2" color="text.secondary">
            O trecho e a página não mudam; só dá para ajustar onde a regra vale e o que ela faz.
          </Typography>
          <TextField
            label="Onde vale (escopo)"
            value={escopoEditado}
            onChange={(evento) => {
              definirEscopoEditado(evento.target.value);
            }}
            multiline
            minRows={4}
            size="small"
          />
          <TextField
            label="O que faz (efeito, opcional)"
            value={efeitoEditado}
            onChange={(evento) => {
              definirEfeitoEditado(evento.target.value);
            }}
            multiline
            minRows={3}
            size="small"
          />

          {escopoLido === null || efeitoLido === null ? (
            <Alert severity="warning">Escopo e efeito precisam ser JSON válido.</Alert>
          ) : null}

          {escopoLido !== null && efeitoLido !== null ? (
            <Box>
              <Typography variant="subtitle2">O que muda</Typography>
              {diferencas.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nada mudou ainda.
                </Typography>
              ) : (
                <Box component="ul" aria-label="Diferenças do ajuste" sx={{ m: 0, pl: 2 }}>
                  {diferencas.map((diferenca) => (
                    <li key={diferenca.caminho}>
                      <Typography variant="body2">
                        <strong>{diferenca.caminho}</strong>: {JSON.stringify(diferenca.antes)} →{' '}
                        {JSON.stringify(diferenca.depois)}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          ) : null}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              disabled={acaoEmCurso !== null || diferencas.length === 0}
              onClick={() => {
                if (escopoLido === null || efeitoLido === null) return;
                executar('ajustar', () => aoAjustar(escopoLido.valor, efeitoLido.valor));
              }}
            >
              Confirmar ajuste
            </Button>
            <Button
              variant="text"
              onClick={() => {
                definirAjustando(false);
                definirEscopoEditado(textoDeJson(regra.escopo));
                definirEfeitoEditado(textoDeJson(regra.efeito));
                }}
            >
              Cancelar
            </Button>
          </Stack>
        </Stack>
      )}

      <Stack spacing={1} component="form" aria-label="Rejeitar a regra">
        <TextField
          label="Motivo da rejeição"
          value={motivoDaRejeicao}
          onChange={(evento) => {
            definirMotivoDaRejeicao(evento.target.value);
          }}
          size="small"
          required
        />
        <Box>
          <Button
            variant="outlined"
            color="error"
            disabled={acaoEmCurso !== null || motivoDaRejeicao.trim() === ''}
            onClick={() => {
              executar('rejeitar', () => aoRejeitar(motivoDaRejeicao.trim()));
            }}
          >
            Rejeitar
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}
